import createCache, { type EmotionCache } from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider, type Direction } from '@mui/material/styles';
import { useMemo, type ReactNode } from 'react';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { createAppTheme } from './theme';

// `stylis` is pinned to 4.2.0 (the version bundled by @emotion/cache): a newer prefixer crashes emotion's
// pipeline on selectors that need lifting (e.g. ::placeholder). See theme.test.tsx.
const caches: Record<Direction, EmotionCache> = {
  ltr: createCache({ key: 'mui', prepend: true }),
  rtl: createCache({ key: 'muirtl', prepend: true, stylisPlugins: [prefixer, rtlPlugin] }),
};

export interface AppThemeProviderProps {
  /** Tenant brand colour (hex). */
  primary?: string | undefined;
  direction?: Direction;
  /** MUI / MUI X locale objects (e.g. frFR from @mui/material/locale). */
  locales?: object[];
  children: ReactNode;
}

/** Emotion cache (LTR / RTL via stylis-plugin-rtl) + MUI theme from the design tokens + CssBaseline. */
export function AppThemeProvider({ primary, direction = 'ltr', locales, children }: AppThemeProviderProps) {
  const theme = useMemo(() => createAppTheme({ primary, direction, locales }), [primary, direction, locales]);
  return (
    <CacheProvider value={caches[direction]}>
      <ThemeProvider theme={theme} defaultMode="light" disableTransitionOnChange>
        <CssBaseline enableColorScheme />
        {children}
      </ThemeProvider>
    </CacheProvider>
  );
}
